import * as t from "io-ts";
import * as tPromise from "io-ts-promise";
import { createSlice } from "@reduxjs/toolkit";
import axios, { getData } from "../../../axios";
import { createErrorReportingAsyncThunk } from "../../helpers";
import {
  createInvoicingOverviewTypes,
  bulkInvoiceResultT,
  CargoInvoiceStatus,
  InvoicingSetStatusValue,
} from "dora-contracts";

const types = createInvoicingOverviewTypes({ decimal: t.string });

export type InvoicingLineRow = t.TypeOf<typeof types.invoicingLineRowT>;
export type OutgoingInvoiceRow = t.TypeOf<typeof types.outgoingInvoiceRowT>;
export type BulkResult = t.TypeOf<typeof bulkInvoiceResultT>;

const prefix = "data/invoicing";

// The Ready/Uninvoiced work-queue tabs and the no-payment toggle load line
// rows with different status filters, potentially at the same time (or in
// quick succession as the user switches tabs), so each gets its own slot —
// sharing one "workQueue" slot between Ready and Uninvoiced let a tab switch
// briefly render the other tab's stale, wrong-status rows before its own
// fetch resolved.
export type LineSlot = "workQueueReady" | "workQueueOpen" | "noPayment";

interface State {
  lines: Record<LineSlot, InvoicingLineRow[] | null>;
  outgoingInvoices: OutgoingInvoiceRow[] | null;
}

const initialState: State = {
  lines: { workQueueReady: null, workQueueOpen: null, noPayment: null },
  outgoingInvoices: null,
};

export const loadInvoicingLines = createErrorReportingAsyncThunk(
  `${prefix}/loadInvoicingLines`,
  async (input: {
    slot: LineSlot;
    statuses: CargoInvoiceStatus[];
    from: string;
    to: string;
    clientId?: string | null;
  }) => {
    const params = new URLSearchParams({
      statuses: input.statuses.join(","),
      from: input.from,
      to: input.to,
    });
    if (input.clientId) {
      params.set("clientId", input.clientId);
    }
    const rows = await axios
      .get(`/api/invoicing/cargos?${params}`)
      .then(getData)
      .then(tPromise.decode(types.invoicingLineRowsT));
    return { slot: input.slot, rows };
  }
);

export const loadOutgoingInvoices = createErrorReportingAsyncThunk(
  `${prefix}/loadOutgoingInvoices`,
  async (input: { from: string; to: string }) => {
    const params = new URLSearchParams({ from: input.from, to: input.to });
    return axios
      .get(`/api/invoicing/outgoing?${params}`)
      .then(getData)
      .then(tPromise.decode(types.outgoingInvoiceRowsT));
  }
);

export const setInvoiceStatuses = createErrorReportingAsyncThunk(
  `${prefix}/setInvoiceStatuses`,
  async (input: { cargoIds: string[]; status: InvoicingSetStatusValue }) => {
    await axios.post("/api/invoicing/set-status", input);
  }
);

export const bulkInvoice = createErrorReportingAsyncThunk(
  `${prefix}/bulkInvoice`,
  async (input: {
    cargoIds: string[];
    dueDate: string | null;
  }): Promise<BulkResult> => {
    return axios
      .post("/api/invoicing/bulk-invoice", input)
      .then(getData)
      .then(tPromise.decode(bulkInvoiceResultT));
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadInvoicingLines.fulfilled, (state, action) => {
        state.lines[action.payload.slot] = action.payload.rows;
      })
      .addCase(loadOutgoingInvoices.fulfilled, (state, action) => {
        state.outgoingInvoices = action.payload;
      });
  },
});

export default slice.reducer;
